import React, { createContext, useContext, useReducer, useEffect } from 'react';

const CartContext = createContext();

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_TO_CART':
      const existingItem = state.items.find(item => item.id === action.payload.id);
      if (existingItem) {
        // A download only needs to be bought once
        if (existingItem.variant === 'download') return state;
        return {
          ...state,
          items: state.items.map(item =>
            item.id === action.payload.id
              ? { ...item, quantity: item.quantity + 1 }
              : item
          )
        };
      }
      return {
        ...state,
        items: [...state.items, { ...action.payload, quantity: 1 }]
      };

    case 'REMOVE_FROM_CART':
      return {
        ...state,
        items: state.items.filter(item => item.id !== action.payload)
      };

    case 'UPDATE_QUANTITY':
      return {
        ...state,
        items: state.items.map(item =>
          item.id === action.payload.id
            ? { ...item, quantity: action.payload.quantity }
            : item
        ).filter(item => item.quantity > 0)
      };

    case 'CLEAR_CART':
      return {
        ...state,
        items: []
      };

    case 'LOAD_CART':
      return {
        ...state,
        items: action.payload || []
      };

    default:
      return state;
  }
};

// Remove event tickets whose date has passed or that are missing a date.
// Tickets remain purchasable until midnight on the day of the event.
const removePastEvents = (items) => {
  const now = new Date();
  return items.filter(item => {
    if (item.type === 'event' || item.category === 'Event' || item.eventDate) {
      if (!item.eventDate) return false; // event without a date — stale
      const eventDay = new Date(item.eventDate);
      eventDay.setHours(23, 59, 59, 999);
      return eventDay >= now;
    }
    return true;
  });
};

// Initialize state with localStorage data if available
const getInitialState = () => {
  try {
    const savedCart = localStorage.getItem('ideaFarmCart');
    if (savedCart) {
      const cartItems = JSON.parse(savedCart);
      const validItems = removePastEvents(Array.isArray(cartItems) ? cartItems : []);
      return {
        items: validItems,
        isOpen: false
      };
    }
  } catch (error) {
    console.error('Error loading cart from localStorage:', error);
    localStorage.removeItem('ideaFarmCart');
  }
  return {
    items: [],
    isOpen: false
  };
};

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, getInitialState());

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    try {
      if (state.items.length > 0) {
        localStorage.setItem('ideaFarmCart', JSON.stringify(state.items));
      } else {
        localStorage.removeItem('ideaFarmCart');
      }
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  }, [state.items]);

  const addToCart = (product) => {
    dispatch({ type: 'ADD_TO_CART', payload: product });
  };

  const removeFromCart = (productId) => {
    dispatch({ type: 'REMOVE_FROM_CART', payload: productId });
  };

  const updateQuantity = (productId, quantity) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { id: productId, quantity } });
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
  };

  const getCartTotal = () => {
    return state.items.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCartItemCount = () => {
    return state.items.reduce((total, item) => total + item.quantity, 0);
  };

  const value = {
    items: state.items,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartItemCount
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
